<template>
    <div class="col-md-12">
        <div class="card">
            <div class="card-header pb-0 d-flex justify-content-between align-items-center no-print">
                <div>
                    <h4>Orden de Trabajo</h4>
                    <span v-if="store.folio" class="text-muted me-3"><strong>Folio:</strong> {{ store.folio }}</span>
                    <span>Descargue e imprima la orden de trabajo para enviarla a la aseguradora</span>
                </div>
                <div class="btn-group" role="group">
                    <button type="button" class="btn btn-sm btn-primary" :disabled="!snap" @click="downloadPDF">
                        <vue-feather type="download" size="14"></vue-feather> Descargar
                    </button>
                    <button type="button" class="btn btn-sm btn-secondary" :disabled="!snap" @click="print">
                        <vue-feather type="printer" size="14"></vue-feather> Imprimir
                    </button>
                </div>
            </div>

            <div class="card-body">
                <div v-if="!snap" class="alert alert-warning">
                    No hay datos de cotización disponibles. Regrese al paso anterior y presione "Confirmar y tramitar".
                </div>

                <!-- Sección reducida: sólo visible al imprimir o generar PDF -->
                <div v-if="snap" ref="pdfReduced" class="reduced-print-view">
                    <div class="d-flex justify-content-between align-items-start mb-3">
                        <img :src="require('@/assets/images/logo/logo-login.png')" alt="SIMM Logo" style="height: 50px;">
                        <div class="text-end">
                            <h5 class="mb-1">Orden de Trabajo</h5>
                            <p class="mb-0"><strong>Folio:</strong> {{ store.folio || 'Por asignar' }}</p>
                            <p class="mb-0"><strong>Fecha:</strong> {{ today }}</p>
                        </div>
                    </div>
                    <hr>
                    <table class="table table-sm table-borderless">
                        <tbody>
                            <tr>
                                <td width="30%"><strong>Aseguradora:</strong></td>
                                <td>{{ snap.producto?.aseguradora?.nombre || '-' }}</td>
                            </tr>
                            <tr>
                                <td><strong>Producto:</strong></td>
                                <td>{{ snap.producto?.producto?.nombre || '-' }}</td>
                            </tr>
                            <tr v-if="snap.cotizacion?.notasOrdenDeTrabajo">
                                <td><strong>Notas:</strong></td>
                                <td style="white-space: pre-wrap;">{{ snap.cotizacion.notasOrdenDeTrabajo }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="mt-4 pt-3 border-top text-center text-muted">
                        <small>Documento generado el {{ today }} · SIMM v.2.0</small>
                    </div>
                </div>

                <div v-if="snap" class="order-details" ref="pdfContent">
                    <!-- Header -->
                    <div class="row mb-4 align-items-center">
                        <div class="col-md-6">
                            <img :src="require('@/assets/images/logo/logo-login.png')" alt="SIMM Logo" style="height: 60px;">
                        </div>
                        <div class="col-md-6 text-end">
                            <h3 class="mb-1">Orden de Trabajo</h3>
                            <p class="mb-1"><strong>Póliza:</strong> {{ snap.cotizacion.poliza || 'Por asignar' }}</p>
                            <p class="mb-0 text-muted"><small>Generado: {{ formatDate(snap._fechaCaptura) }}</small></p>
                        </div>
                    </div>
                    <hr>

                    <!-- Datos del Producto -->
                    <div class="section mb-4">
                        <h5 class="section-title">Datos del Producto</h5>
                        <div class="row">
                            <div class="col-md-6">
                                <table class="table table-sm table-borderless">
                                    <tbody>
                                        <tr>
                                            <td width="45%"><strong>Aseguradora:</strong></td>
                                            <td>{{ snap.producto.aseguradora?.nombre || '-' }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Producto:</strong></td>
                                            <td>{{ snap.producto.producto?.nombre || '-' }}</td>
                                        </tr>
                                        <tr v-if="snap.producto.ramo">
                                            <td><strong>Ramo:</strong></td>
                                            <td>{{ snap.producto.ramo?.nombre }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="col-md-6">
                                <table class="table table-sm table-borderless">
                                    <tbody>
                                        <tr v-if="snap.producto.plazo">
                                            <td width="45%"><strong>Plazo:</strong></td>
                                            <td>{{ snap.producto.plazo?.nombre }}</td>
                                        </tr>
                                        <tr v-if="snap.producto.tipo">
                                            <td><strong>Tipo:</strong></td>
                                            <td>{{ snap.producto.tipo?.nombre }}</td>
                                        </tr>
                                        <tr v-if="snap.producto.divisa">
                                            <td><strong>Divisa:</strong></td>
                                            <td>{{ snap.producto.divisa?.nombre }}<span v-if="snap.producto.divisa?.simbolo"> ({{ snap.producto.divisa.simbolo }})</span></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <hr>

                    <!-- Datos de la Cotización -->
                    <div class="section mb-4">
                        <h5 class="section-title">Datos de la Cotización</h5>
                        <div class="row">
                            <div class="col-md-6">
                                <table class="table table-sm table-borderless">
                                    <tbody>
                                        <tr>
                                            <td width="50%"><strong>Número de póliza:</strong></td>
                                            <td>{{ snap.cotizacion.poliza || 'Por asignar' }}</td>
                                        </tr>
                                        <tr v-if="snap.cotizacion.extensiones?.fechaSolicitud">
                                            <td><strong>Fecha de solicitud:</strong></td>
                                            <td>{{ snap.cotizacion.extensiones.fechaSolicitud }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Inicio de vigencia:</strong></td>
                                            <td>{{ snap.cotizacion.inicioVigencia || '-' }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Fin de vigencia:</strong></td>
                                            <td>{{ snap.cotizacion.finVigencia || '-' }}</td>
                                        </tr>
                                        <tr v-if="snap.cotizacion.extensiones?.diasContratados != null">
                                            <td><strong>Días contratados:</strong></td>
                                            <td>{{ snap.cotizacion.extensiones.diasContratados }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="col-md-6">
                                <table class="table table-sm table-borderless">
                                    <tbody>
                                        <tr>
                                            <td width="50%"><strong>Frecuencia de pago:</strong></td>
                                            <td>{{ snap.cotizacion.periodoPago?.nombre || '-' }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Deducible:</strong></td>
                                            <td>{{ formatCurrency(snap.cotizacion.deducible) }}</td>
                                        </tr>
                                        <tr v-if="snap.cotizacion.extensiones?.sumaAsegurada != null">
                                            <td><strong>Suma asegurada:</strong></td>
                                            <td>{{ formatCurrencyOrText(snap.cotizacion.extensiones.sumaAsegurada) }}</td>
                                        </tr>
                                        <tr v-if="snap.cotizacion.extensiones?.coaseguro != null">
                                            <td><strong>Coaseguro:</strong></td>
                                            <td>{{ formatCurrency(snap.cotizacion.extensiones.coaseguro) }}</td>
                                        </tr>
                                        <tr v-if="snap.cotizacion.extensiones?.topeCoaseguro != null">
                                            <td><strong>Tope de coaseguro:</strong></td>
                                            <td>{{ formatCurrency(snap.cotizacion.extensiones.topeCoaseguro) }}</td>
                                        </tr>
                                        <tr v-if="snap.cotizacion.extensiones?.conversionGarantizada != null">
                                            <td><strong>Conversión garantizada:</strong></td>
                                            <td>{{ snap.cotizacion.extensiones.conversionGarantizada === 'true' || snap.cotizacion.extensiones.conversionGarantizada === true ? 'Sí' : 'No' }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <hr>

                    <!-- Coberturas adicionales (FlexPlus) -->
                    <div v-if="snap.cotizacion.extensiones?.coberturasSeleccionadas?.length" class="section mb-4">
                        <h5 class="section-title">Coberturas Adicionales</h5>
                        <ul class="list-unstyled">
                            <li v-for="id in snap.cotizacion.extensiones.coberturasSeleccionadas" :key="id" class="mb-1">
                                <vue-feather type="check" size="14" class="text-success me-1"></vue-feather>
                                {{ coberturaNombre(id) }}
                            </li>
                        </ul>
                        <hr>
                    </div>

                    <!-- Asegurados -->
                    <div class="section mb-4">
                        <h5 class="section-title">Asegurados</h5>
                        <div class="table-responsive">
                            <table class="table table-bordered table-sm">
                                <thead class="table-light">
                                    <tr>
                                        <th>Nombre</th>
                                        <th class="text-center">Edad</th>
                                        <th class="text-center">Género</th>
                                        <th class="text-center">Parentesco</th>
                                        <th class="text-end">Antigüedad</th>
                                        <th class="text-end">Fecha alta</th>
                                        <th class="text-end">Fecha baja</th>
                                        <th class="text-end">Prima</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="a in snap.asegurados" :key="a.id">
                                        <td>{{ a.nombre }} {{ a.paterno }} {{ a.materno }}</td>
                                        <td class="text-center">{{ a.edad }}</td>
                                        <td class="text-center">{{ a.genero }}</td>
                                        <td class="text-center">{{ a.parentesco }}</td>
                                        <td class="text-end">{{ a.antiguedad || '-' }}</td>
                                        <td class="text-end">{{ a.fechaAlta || '-' }}</td>
                                        <td class="text-end">{{ a.fechaFin || '-' }}</td>
                                        <td class="text-end">{{ formatCurrency(a.prima) }}</td>
                                    </tr>
                                    <tr>
                                        <td colspan="7">Prima neta</td>
                                        <td class="text-end fw-bold">{{ formatCurrency(snap.cotizacion.primas.sumaPrimas) }}</td>
                                    </tr>
                                    <tr>
                                        <td colspan="7">Derecho de póliza</td>
                                        <td class="text-end">{{ formatCurrency(snap.cotizacion.primas.derechoPoliza) }}</td>
                                    </tr>
                                    <tr>
                                        <td colspan="7">IVA</td>
                                        <td class="text-end">{{ formatCurrency(snap.cotizacion.primas.montoIva) }}</td>
                                    </tr>
                                    <tr class="table-secondary">
                                        <td colspan="7" class="fw-bold">Prima total</td>
                                        <td class="text-end fw-bold">{{ formatCurrency(snap.cotizacion.primas.primaTotal) }}</td>
                                    </tr>
                                    <tr>
                                        <td colspan="7" class="text-primary fw-bold">Prima periódica ({{ snap.cotizacion.periodoPago?.nombre || '-' }})</td>
                                        <td class="text-end text-primary fw-bold">{{ formatCurrency(snap.cotizacion.primas.primaPeriodica) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <hr>

                    <!-- Contratante -->
                    <div class="section mb-4">
                        <h5 class="section-title">Contratante</h5>
                        <div class="row">
                            <div class="col-md-6">
                                <table class="table table-sm table-borderless">
                                    <tbody>
                                        <tr>
                                            <td width="40%"><strong>Nombre:</strong></td>
                                            <td>{{ snap.contratante.nombre }} {{ snap.contratante.paterno }} {{ snap.contratante.materno }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>RFC:</strong></td>
                                            <td>{{ snap.contratante.rfc || '-' }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>CURP:</strong></td>
                                            <td>{{ snap.contratante.curp || '-' }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Fecha de nacimiento:</strong></td>
                                            <td>{{ snap.contratante.fechaNacimiento || '-' }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-if="snap.contratante.domicilios?.length" class="col-md-6">
                                <strong>Domicilios:</strong>
                                <div v-for="(d, i) in snap.contratante.domicilios" :key="i" class="mt-1 small">
                                    {{ d.calle }} {{ d.numeroExt }}<span v-if="d.numeroInt"> int. {{ d.numeroInt }}</span>,
                                    {{ d.colonia }}, {{ d.municipio }}, {{ d.estado }}, C.P. {{ d.codigoPostal }}
                                </div>
                            </div>
                        </div>
                        <div v-if="snap.contratante.mediosContacto?.length" class="row mt-2">
                            <div class="col-md-12">
                                <strong>Medios de contacto:</strong>
                                <div class="d-flex flex-wrap gap-3 mt-1">
                                    <span v-for="(m, i) in snap.contratante.mediosContacto" :key="i" class="small">
                                        {{ m.tipo?.nombre || m.tipo?.id }}: <strong>{{ m.valor }}</strong>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr>

                    <!-- Información Laboral -->
                    <div v-if="snap.laboral.organizacion || snap.laboral.unidad || snap.laboral.lugarTrabajo" class="section mb-4">
                        <h5 class="section-title">Información Laboral</h5>
                        <div class="row">
                            <div class="col-md-6">
                                <table class="table table-sm table-borderless">
                                    <tbody>
                                        <tr v-if="snap.laboral.organizacion">
                                            <td width="40%"><strong>Organización:</strong></td>
                                            <td>{{ snap.laboral.organizacion?.nombre }}</td>
                                        </tr>
                                        <tr v-if="snap.laboral.unidad">
                                            <td><strong>Unidad:</strong></td>
                                            <td>{{ snap.laboral.unidad?.nombre }}</td>
                                        </tr>
                                        <tr v-if="snap.laboral.lugarTrabajo">
                                            <td><strong>Lugar de trabajo:</strong></td>
                                            <td>{{ snap.laboral.lugarTrabajo?.nombre }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="col-md-6">
                                <table class="table table-sm table-borderless">
                                    <tbody>
                                        <tr v-if="snap.laboral.referencia">
                                            <td width="40%"><strong>Referencia:</strong></td>
                                            <td>{{ snap.laboral.referencia }}</td>
                                        </tr>
                                        <tr v-if="snap.laboral.enroler">
                                            <td><strong>Enróler:</strong></td>
                                            <td>{{ snap.laboral.enroler?.nombre }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <hr>
                    </div>

                    <!-- Notas -->
                    <div v-if="snap.cotizacion.notasOrdenDeTrabajo" class="section mb-4">
                        <h5 class="section-title">Notas de Orden de Trabajo</h5>
                        <p class="mb-0" style="white-space: pre-wrap;">{{ snap.cotizacion.notasOrdenDeTrabajo }}</p>
                    </div>
                    <div v-if="snap.cotizacion.observacionesInternas" class="section mb-4 no-print">
                        <h5 class="section-title">Observaciones Internas</h5>
                        <p class="mb-0 text-muted fst-italic" style="white-space: pre-wrap;">{{ snap.cotizacion.observacionesInternas }}</p>
                    </div>

                    <!-- Footer -->
                    <div class="print-footer mt-5 pt-3 border-top text-center text-muted">
                        <small>Documento generado el {{ new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) }} · SIMM v.2.0</small>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useSeguroStore } from '@/stores/seguroStore';
import html2pdf from 'html2pdf.js';

const COBERTURAS_NOMBRES = {
    medicamentos_fuera_hospital: 'Medicamentos fuera de hospital',
    emergencia_extranjero: 'Emergencia en el extranjero',
    emergencia_catastroficas: 'Emergencia más catastróficas',
    atencion_medica_completa: 'Atención médica completa',
    complicaciones_no_cubiertas: 'Complicaciones médicas no cubiertas',
    maternidad_extendida: 'Maternidad extendida',
    deducible_cero_accidente: 'Deducible cero por accidente',
    cobertura_nacional: 'Cobertura nacional',
    cliente_distinguido: 'Cliente distinguido',
    proteccion_dental_integral: 'Protección dental integral',
    asistencia_viajes: 'Asistencia en viajes',
    preexistencias: 'Preexistencias',
    cobertura_extranjero: 'Cobertura en el extranjero',
};

export default {
    setup() {
        const store = useSeguroStore();
        return { store };
    },
    computed: {
        snap() {
            return this.store.cotizacionSnapshot;
        },
        today() {
            return new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
        },
    },
    methods: {
        coberturaNombre(id) {
            return COBERTURAS_NOMBRES[id] || id;
        },
        formatCurrency(value) {
            if (value == null || value === '') return '-';
            return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
        },
        formatCurrencyOrText(value) {
            if (value === 'Ilimitada') return 'Ilimitada';
            return this.formatCurrency(value);
        },
        formatDate(iso) {
            if (!iso) return '-';
            return new Date(iso).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
        },
        print() {
            window.print();
        },
        async downloadPDF() {
            const element = this.$refs.pdfReduced;
            if (!element) return;
            try {
                element.style.display = 'block';
                const folio = this.store.folio || 'sin-folio';
                const fecha = new Date().toISOString().split('T')[0];
                const opt = {
                    margin: [10, 10, 10, 10],
                    filename: `Orden_Trabajo_${folio}_${fecha}.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true },
                    jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait', compress: true },
                    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
                };
                await html2pdf().set(opt).from(element).save();
            } catch {
                alert('Hubo un error al generar el PDF. Por favor, intente nuevamente.');
            } finally {
                element.style.display = 'none';
            }
        },
    },
};
</script>

<style scoped>
.section-title {
    color: #2c3e50;
    border-bottom: 2px solid #3498db;
    padding-bottom: 8px;
    margin-bottom: 15px;
}

.order-details {
    font-size: 14px;
    background-color: white;
    padding: 20px;
}

.table-borderless td {
    padding: 5px 8px;
}

.section {
    page-break-inside: avoid;
}

hr {
    margin: 1rem 0;
    border: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.reduced-print-view {
    display: none;
}

@media print {
    .no-print { display: none !important; }
    .order-details { display: none !important; }
    .reduced-print-view { display: block !important; }

    .card { border: none !important; box-shadow: none !important; }
    .card-body { padding: 0 !important; }

    body {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
    }

    hr { border-top: 1px solid #dee2e6 !important; }
}
</style>
